import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { LayoutDashboard, Ticket, HandCoins, Wallet, Users, Building2, Settings, LogOut, Menu, PanelLeftClose, PanelLeftOpen, ListPlus, ScanFace, Shirt, ChevronRight, ChevronDown, Home } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { CloverLogo } from "./CloverLogo";

const GROUPS = [
  { label: null, items: [{ to: "/", label: "Beranda", icon: LayoutDashboard, key: "dashboard" }] },
  { label: "Operasional", items: [
    { to: "/voucher", label: "Voucher", icon: Ticket, key: "voucher" },
    { to: "/voucher/massal", label: "Input Massal", icon: ListPlus, key: "massal" },
    { to: "/kasbon", label: "Kasbon", icon: HandCoins, key: "kasbon" },
    { to: "/peralatan", label: "Peralatan", icon: Shirt, key: "peralatan" },
  ] },
  { label: "Keuangan", items: [{ to: "/gajian", label: "Gajian", icon: Wallet, key: "gajian" }] },
  { label: "Data", items: [
    { to: "/anak", label: "Anak", icon: Users, key: "anak" },
    { to: "/perusahaan", label: "Perusahaan", icon: Building2, key: "perusahaan" },
    { to: "/kehadiran", label: "Kehadiran", icon: ScanFace, key: "kehadiran" },
  ] },
  { label: "Sistem", items: [{ to: "/pengaturan", label: "Pengaturan", icon: Settings, key: "pengaturan" }] },
];
const NAV = GROUPS.flatMap((g) => g.items.map((i) => ({ ...i, group: g.label })));
const BOTTOM = ["dashboard", "voucher", "kasbon", "gajian"];
const EXTRA = [[/^\/anak\/.+/, "Anak", "Detail anak", "/anak"], [/^\/slip\//, "Gajian", "Slip gaji", "/gajian"]];

const crumbsFor = (path) => {
  const ex = EXTRA.find(([re]) => re.test(path));
  if (ex) {
    const parent = NAV.find((i) => i.to === ex[3]);
    return [parent.group, { label: ex[1], to: ex[3] }, ex[2]];
  }
  const item = NAV.find((i) => i.to === path) || NAV[0];
  return [item.group, item.label];
};

const Brand = ({ compact }) => (
  <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home-link">
    <CloverLogo className="h-9 w-9 shrink-0" testid="brand-logo" />
    {!compact && (
      <div className="leading-tight">
        <p className="font-heading text-[15px] font-semibold tracking-[0.08em] text-gray-900">CLOVER</p>
        <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-gray-400">Management</p>
      </div>
    )}
  </Link>
);

const SideLink = ({ item, compact, onClick, badge, prefix = "" }) => (
  <NavLink
    to={item.to}
    end
    onClick={onClick}
    title={compact ? item.label : undefined}
    data-testid={`${prefix}nav-${item.key}`}
    className={({ isActive }) =>
      `relative flex h-10 items-center gap-3 rounded-lg px-3 text-[14px] font-medium transition-colors duration-150 ${
        isActive ? "bg-rose-50 text-primary before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-r before:bg-[#E11D5A]" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
      } ${compact ? "justify-center" : ""}`
    }
  >
    <item.icon className="h-[18px] w-[18px] shrink-0" />
    {!compact && <span className="flex-1">{item.label}</span>}
    {badge > 0 && <span data-testid={`${prefix}nav-badge-${item.key}`} className={`rounded-full bg-[#E11D5A] px-2 py-0.5 text-[11px] font-semibold text-white ${compact ? "absolute right-1 top-0.5 px-1.5" : ""}`}>{badge}</span>}
  </NavLink>
);

const NavGroups = ({ compact, badgeFor, onClick, prefix = "" }) => GROUPS.map((g, gi) => (
  <div key={gi} className={gi ? "mt-4" : ""}>
    {g.label && (compact ? <div className="mx-3 mb-2 border-t border-gray-100" /> :
      <p className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400" data-testid={`${prefix}nav-group-${g.label.toLowerCase()}`}>{g.label}</p>)}
    <div className="space-y-0.5">
      {g.items.map((i) => <SideLink key={i.key} item={i} prefix={prefix} compact={compact} onClick={onClick} badge={badgeFor(i.key)} />)}
    </div>
  </div>
));

const ProfileMenu = ({ user, onLogout }) => {
  const email = user?.email || "admin";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="profile-menu-trigger" className="flex h-10 items-center gap-2 rounded-full border border-gray-200 bg-white py-1 pl-1 pr-2.5 transition-colors hover:bg-gray-50">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">{email[0].toUpperCase()}</span>
          <span className="hidden text-left leading-tight sm:block">
            <span className="block text-[13px] font-semibold text-gray-900">Admin</span>
            <span className="block text-[11px] text-gray-500">{email}</span>
          </span>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid="profile-menu">
        <DropdownMenuLabel className="font-normal"><p className="text-sm font-semibold">Admin</p><p className="text-xs text-gray-500">{email}</p></DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild><Link to="/pengaturan" data-testid="profile-menu-settings"><Settings className="mr-2 h-4 w-4" />Pengaturan</Link></DropdownMenuItem>
        <DropdownMenuItem onClick={onLogout} data-testid="profile-menu-logout" className="text-red-600 focus:text-red-700"><LogOut className="mr-2 h-4 w-4" />Keluar</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Crumbs = ({ path }) => {
  const parts = crumbsFor(path);
  const last = parts[parts.length - 1];
  return (
    <div className="min-w-0">
      <nav className="flex flex-wrap items-center gap-1 text-[12px] text-gray-400" data-testid="breadcrumb">
        <Link to="/" className="flex items-center gap-1 hover:text-gray-600"><Home className="h-3.5 w-3.5" />Home</Link>
        {parts.filter(Boolean).map((p, i) => (
          <span key={i} className="flex items-center gap-1"><ChevronRight className="h-3 w-3" />
            {typeof p === "object" ? <Link to={p.to} className="hover:text-gray-600">{p.label}</Link> : <span className={i === parts.filter(Boolean).length - 1 ? "text-gray-600" : ""}>{p}</span>}
          </span>
        ))}
      </nav>
      <p className="text-[15px] font-semibold text-gray-900" data-testid="topbar-title">{last}</p>
    </div>
  );
};

export default function Layout() {
  const [compact, setCompact] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = () => { logout(); navigate("/login"); };
  const loc = useLocation();
  const [cekCount, setCekCount] = useState(0);
  useEffect(() => { api.get("/peralatan/perlu-cek-count").then((r) => setCekCount(r.data.count)).catch(() => {}); }, [loc.pathname]);
  const badgeFor = (key) => (key === "peralatan" ? cekCount : 0);
  const today = new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-background">
      <aside className={`no-print fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-gray-100 bg-white transition-[width] duration-200 md:flex ${compact ? "w-[72px]" : "w-56"}`}>
        <div className={`flex h-16 items-center px-4 ${compact ? "justify-center" : "justify-between"}`}>
          <Brand compact={compact} />
          {!compact && (
            <button data-testid="sidebar-collapse-btn" onClick={() => setCompact(true)} className="rounded-md p-2 text-gray-400 hover:bg-gray-50" aria-label="Ciutkan" title="Ciutkan menu">
              <PanelLeftClose className="h-4 w-4" />
            </button>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto px-3 pb-4 pt-2">
          {compact && (
            <button data-testid="sidebar-expand-btn" onClick={() => setCompact(false)} className="mb-3 flex h-10 w-full items-center justify-center rounded-lg text-gray-400 hover:bg-gray-50" aria-label="Lebarkan" title="Lebarkan menu">
              <PanelLeftOpen className="h-5 w-5" />
            </button>
          )}
          <NavGroups compact={compact} badgeFor={badgeFor} />
        </nav>
        <div className="border-t border-gray-100 p-3">
          <button data-testid="logout-btn" onClick={doLogout} title="Keluar" className={`flex h-10 w-full items-center gap-3 rounded-lg px-3 text-[14px] font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 ${compact ? "justify-center" : ""}`}>
            <LogOut className="h-[18px] w-[18px]" />
            {!compact && "Keluar"}
          </button>
        </div>
      </aside>

      <div className={`transition-[padding] duration-200 ${compact ? "md:pl-[72px]" : "md:pl-56"}`}>
        <header className="no-print sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-gray-100 bg-white/90 px-4 backdrop-blur-xl sm:px-6">
          <div className="md:hidden"><Brand /></div>
          <div className="hidden md:block"><Crumbs path={loc.pathname} /></div>
          <div className="flex items-center gap-3">
            <span className="hidden text-[13px] text-gray-500 lg:inline" data-testid="topbar-date">{today}</span>
            <ProfileMenu user={user} onLogout={doLogout} />
          </div>
        </header>

        <main className="pb-24 md:pb-10">
          <div className="mx-auto max-w-[1440px] px-4 py-5 sm:px-6 lg:py-7">
            <Outlet />
          </div>
        </main>
      </div>

      <nav className="no-print fixed inset-x-0 bottom-0 z-50 flex h-16 items-stretch justify-around border-t border-gray-100 bg-white/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_20px_-8px_rgba(16,24,40,0.12)] backdrop-blur-xl md:hidden">
        {NAV.filter((i) => BOTTOM.includes(i.key)).map((i) => (
          <NavLink key={i.key} to={i.to} end data-testid={`bottom-nav-${i.key}`}
            className={({ isActive }) => `flex min-w-[44px] flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium active:scale-95 ${isActive ? "text-primary" : "text-gray-500"}`}>
            <i.icon className="h-5 w-5" />
            {i.label}
          </NavLink>
        ))}
        <button data-testid="bottom-nav-menu" onClick={() => setMenuOpen(true)} className="flex min-w-[44px] flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium text-gray-500 active:scale-95">
          <span className="relative"><Menu className="h-5 w-5" />{cekCount > 0 && <span data-testid="bottom-nav-menu-badge" className="absolute -right-2 -top-1 h-2.5 w-2.5 rounded-full bg-[#E11D5A]" />}</span>
          Lainnya
        </button>
      </nav>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl pb-8" data-testid="mobile-menu-sheet">
          <SheetHeader className="mb-3 text-left"><SheetTitle className="font-heading">Menu</SheetTitle></SheetHeader>
          <NavGroups badgeFor={badgeFor} onClick={() => setMenuOpen(false)} prefix="m-" />
          <button data-testid="mobile-logout-btn" onClick={doLogout} className="mt-4 flex h-11 w-full items-center gap-3 rounded-lg px-3 text-[14px] font-medium text-red-600 hover:bg-red-50">
            <LogOut className="h-5 w-5" /> Keluar
          </button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
